/**
 * Floor-plan polygon + label coordinate helpers (shared frontend/backend).
 */

/**
 * Map a viewport (client) point to SVG user-space coordinates.
 * Accounts for preserveAspectRatio letterboxing on the SVG element.
 *
 * @param {SVGSVGElement|null|undefined} svgElement
 * @param {number} clientX
 * @param {number} clientY
 * @param {{ width?: number, height?: number }} [bounds] optional clamp bounds (defaults to viewBox)
 * @returns {{ x: number, y: number } | null}
 */
export function clientPointToSvgCoords(svgElement, clientX, clientY, bounds) {
  if (!svgElement?.createSVGPoint) return null;
  const pt = svgElement.createSVGPoint();
  pt.x = clientX;
  pt.y = clientY;
  const ctm = svgElement.getScreenCTM();
  if (!ctm) return null;
  const svgPt = pt.matrixTransform(ctm.inverse());
  const viewBox = svgElement.viewBox?.baseVal;
  const maxW = bounds?.width ?? viewBox?.width;
  const maxH = bounds?.height ?? viewBox?.height;
  if (!Number.isFinite(maxW) || !Number.isFinite(maxH) || maxW <= 0 || maxH <= 0) {
    return { x: Math.round(svgPt.x), y: Math.round(svgPt.y) };
  }
  return {
    x: Math.round(Math.max(0, Math.min(maxW, svgPt.x))),
    y: Math.round(Math.max(0, Math.min(maxH, svgPt.y)))
  };
}

/**
 * @param {string|null|undefined} value space-separated "x,y" pairs
 * @returns {{ x: number, y: number }[]}
 */
export function parsePolygonPoints(value) {
  if (!value || typeof value !== 'string') return [];
  return value
    .trim()
    .split(/\s+/)
    .map((pair) => {
      const [xs, ys] = pair.split(',');
      const x = Number(xs);
      const y = Number(ys);
      if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
      return { x, y };
    })
    .filter(Boolean);
}

/**
 * @param {{ x: number, y: number }[]} points
 * @returns {string}
 */
export function formatPlanPolygon(points) {
  if (!Array.isArray(points) || points.length === 0) return '';
  return points.map((p) => `${Math.round(p.x)},${Math.round(p.y)}`).join(' ');
}

/**
 * @param {{ x: number, y: number }} point
 * @param {{ x: number, y: number }[]} vertices
 * @returns {number} index of closest vertex, or -1 when empty
 */
export function findClosestVertexIndex(point, vertices) {
  if (!point || !Array.isArray(vertices) || vertices.length === 0) return -1;
  let bestIdx = 0;
  let bestDist = Infinity;
  for (let i = 0; i < vertices.length; i++) {
    const dx = point.x - vertices[i].x;
    const dy = point.y - vertices[i].y;
    const dist = dx * dx + dy * dy;
    if (dist < bestDist) {
      bestDist = dist;
      bestIdx = i;
    }
  }
  return bestIdx;
}

/**
 * @param {{ x: number, y: number }} point
 * @param {{ x: number, y: number }} a
 * @param {{ x: number, y: number }} b
 * @returns {number}
 */
function distanceSquaredPointToSegment(point, a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) {
    const ex = point.x - a.x;
    const ey = point.y - a.y;
    return ex * ex + ey * ey;
  }
  let t = ((point.x - a.x) * dx + (point.y - a.y) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  const projX = a.x + t * dx;
  const projY = a.y + t * dy;
  const ex = point.x - projX;
  const ey = point.y - projY;
  return ex * ex + ey * ey;
}

/**
 * @param {{ x: number, y: number }[]} vertices
 * @param {boolean} closed
 * @returns {{ startIndex: number, endIndex: number }[]}
 */
function getPolygonEdges(vertices, closed) {
  const edges = [];
  if (!Array.isArray(vertices) || vertices.length < 2) return edges;
  const limit = closed ? vertices.length : vertices.length - 1;
  for (let i = 0; i < limit; i++) {
    edges.push({ startIndex: i, endIndex: (i + 1) % vertices.length });
  }
  return edges;
}

/**
 * Pick the polygon edge where a new vertex should be inserted.
 *
 * When `activeIndex` is set, only the two edges adjacent to that vertex are
 * considered (previous→active or active→next). Otherwise all edges are used.
 *
 * @returns {{ anchorIndex: number, start: { x: number, y: number }, end: { x: number, y: number }, isAppend: boolean } | null}
 */
export function resolvePolygonInsertEdge(point, vertices, activeIndex = -1) {
  if (!point || !Array.isArray(vertices) || vertices.length === 0) return null;
  if (vertices.length === 1) {
    return {
      anchorIndex: 0,
      start: { x: vertices[0].x, y: vertices[0].y },
      end: { x: point.x, y: point.y },
      isAppend: true
    };
  }

  const closed = vertices.length >= 3;
  let candidateEdges = [];

  if (activeIndex >= 0 && activeIndex < vertices.length) {
    if (activeIndex > 0 || closed) {
      const prevIndex = closed && activeIndex === 0 ? vertices.length - 1 : activeIndex - 1;
      if (prevIndex >= 0 && prevIndex !== activeIndex) {
        candidateEdges.push({ startIndex: prevIndex, endIndex: activeIndex });
      }
    }
    if (activeIndex < vertices.length - 1 || closed) {
      const nextIndex = (activeIndex + 1) % vertices.length;
      if (nextIndex !== activeIndex) {
        candidateEdges.push({ startIndex: activeIndex, endIndex: nextIndex });
      }
    }
  } else {
    candidateEdges = getPolygonEdges(vertices, closed);
  }

  if (!candidateEdges.length) {
    const last = vertices[vertices.length - 1];
    return {
      anchorIndex: vertices.length - 1,
      start: { x: last.x, y: last.y },
      end: { x: point.x, y: point.y },
      isAppend: true
    };
  }

  let bestEdge = candidateEdges[0];
  let bestDist = Infinity;
  for (const edge of candidateEdges) {
    const dist = distanceSquaredPointToSegment(
      point,
      vertices[edge.startIndex],
      vertices[edge.endIndex]
    );
    if (dist < bestDist) {
      bestDist = dist;
      bestEdge = edge;
    }
  }

  return {
    anchorIndex: bestEdge.startIndex,
    start: { x: vertices[bestEdge.startIndex].x, y: vertices[bestEdge.startIndex].y },
    end: { x: vertices[bestEdge.endIndex].x, y: vertices[bestEdge.endIndex].y },
    isAppend: false
  };
}

/**
 * Preview segments (start→cursor and cursor→end) for the chosen insert edge.
 *
 * @returns {{ x1: number, y1: number, x2: number, y2: number }[]}
 */
export function getPolygonInsertPreviewSegments(point, vertices, activeIndex = -1) {
  const resolved = resolvePolygonInsertEdge(point, vertices, activeIndex);
  if (!resolved) return [];
  if (resolved.isAppend) {
    return [{ x1: resolved.start.x, y1: resolved.start.y, x2: point.x, y2: point.y }];
  }
  return [
    { x1: resolved.start.x, y1: resolved.start.y, x2: point.x, y2: point.y },
    { x1: point.x, y1: point.y, x2: resolved.end.x, y2: resolved.end.y }
  ];
}

/**
 * Insert a new vertex on the edge closest to `point` (respecting active vertex adjacency).
 *
 * @returns {{ vertices: { x: number, y: number }[], activeIndex: number }}
 */
export function addVertexOnInsertEdge(vertices, point, activeIndex = -1) {
  if (!Array.isArray(vertices) || vertices.length === 0) {
    return { vertices: [{ x: point.x, y: point.y }], activeIndex: 0 };
  }
  const resolved = resolvePolygonInsertEdge(point, vertices, activeIndex);
  if (!resolved || resolved.isAppend) {
    const next = vertices.map((p) => ({ x: p.x, y: p.y }));
    next.push({ x: point.x, y: point.y });
    return { vertices: next, activeIndex: next.length - 1 };
  }
  const insertAt = resolved.anchorIndex + 1;
  const next = vertices.map((p) => ({ x: p.x, y: p.y }));
  next.splice(insertAt, 0, { x: point.x, y: point.y });
  return { vertices: next, activeIndex: insertAt };
}

/**
 * Insert a new vertex after the one closest to `point`.
 * Prefer {@link addVertexOnInsertEdge} when an active vertex index is known.
 *
 * @returns {{ vertices: { x: number, y: number }[], activeIndex: number }}
 */
export function addVertexAfterClosest(vertices, point) {
  return addVertexOnInsertEdge(vertices, point, -1);
}

/**
 * @param {number} activeIndex
 * @param {number} removedIndex
 * @param {number} nextLength
 * @returns {number}
 */
export function adjustActiveVertexIndexAfterRemove(activeIndex, removedIndex, nextLength) {
  if (nextLength <= 0) return -1;
  if (activeIndex < 0) return nextLength - 1;
  if (removedIndex < activeIndex) return activeIndex - 1;
  if (removedIndex === activeIndex) return Math.min(removedIndex, nextLength - 1);
  return activeIndex;
}

/**
 * @param {{ x: number, y: number }[]} points
 */
export function polygonCentroid(points) {
  if (!Array.isArray(points) || points.length === 0) {
    return { x: 0, y: 0 };
  }
  let sx = 0;
  let sy = 0;
  for (const p of points) {
    sx += p.x;
    sy += p.y;
  }
  return { x: sx / points.length, y: sy / points.length };
}

/**
 * Ray-casting point-in-polygon test.
 *
 * @param {{ x: number, y: number }} point
 * @param {{ x: number, y: number }[]} vertices
 * @returns {boolean}
 */
export function pointInPolygon(point, vertices) {
  if (!point || !Array.isArray(vertices) || vertices.length < 3) return false;
  let inside = false;
  for (let i = 0, j = vertices.length - 1; i < vertices.length; j = i++) {
    const xi = vertices[i].x;
    const yi = vertices[i].y;
    const xj = vertices[j].x;
    const yj = vertices[j].y;
    const intersects =
      yi > point.y !== yj > point.y &&
      point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

/**
 * Normalize stored room coordinates to SVG-friendly shape.
 *
 * Supports canonical `{ planPolygon, labelCoordinates }` and legacy `{ points, labelX, labelY }`.
 *
 * @returns {{ points: string, labelX?: number, labelY?: number } | null}
 */
export function normalizeRoomCoordinatesForViewer(coords) {
  if (!coords || typeof coords !== 'object') return null;

  const polygonRaw = coords.planPolygon || coords.points || '';
  const points = typeof polygonRaw === 'string' ? polygonRaw.trim() : '';
  if (!points) return null;

  const labelCoords = coords.labelCoordinates || {};
  const labelX =
    labelCoords.x != null
      ? Number(labelCoords.x)
      : coords.labelX != null
        ? Number(coords.labelX)
        : undefined;
  const labelY =
    labelCoords.y != null
      ? Number(labelCoords.y)
      : coords.labelY != null
        ? Number(coords.labelY)
        : undefined;

  const out = { points };
  if (Number.isFinite(labelX) && Number.isFinite(labelY)) {
    out.labelX = labelX;
    out.labelY = labelY;
  }
  return out;
}

/**
 * @param {object|null|undefined} coords
 * @returns {{ planPolygon: string, labelCoordinates?: { x: number, y: number } } | null}
 */
export function toCanonicalRoomCoordinates(coords) {
  if (!coords || typeof coords !== 'object') return null;
  const polygonRaw = coords.planPolygon || coords.points || '';
  const planPolygon = typeof polygonRaw === 'string' ? polygonRaw.trim() : '';
  if (!planPolygon) return null;

  const parsed = parsePolygonPoints(planPolygon);
  if (parsed.length < 3) return null;

  const lc = coords.labelCoordinates || {};
  let x = lc.x != null ? Number(lc.x) : coords.labelX != null ? Number(coords.labelX) : null;
  let y = lc.y != null ? Number(lc.y) : coords.labelY != null ? Number(coords.labelY) : null;
  if (!Number.isFinite(x) || !Number.isFinite(y)) {
    const c = polygonCentroid(parsed);
    x = c.x;
    y = c.y;
  }

  return {
    planPolygon: formatPlanPolygon(parsed),
    labelCoordinates: { x, y }
  };
}

/**
 * @param {string} planPolygon
 * @param {{ width?: number, height?: number }} [bounds]
 */
export function validatePlanPolygon(planPolygon, bounds = {}) {
  const points = parsePolygonPoints(planPolygon);
  if (points.length < 3) {
    return { ok: false, error: 'Polygon needs at least 3 points' };
  }
  const maxW = bounds.width;
  const maxH = bounds.height;
  if (Number.isFinite(maxW) && Number.isFinite(maxH) && maxW > 0 && maxH > 0) {
    for (const p of points) {
      if (p.x < 0 || p.y < 0 || p.x > maxW || p.y > maxH) {
        return { ok: false, error: 'Polygon points must be within the plan image bounds' };
      }
    }
  }
  return { ok: true, value: formatPlanPolygon(points) };
}
